import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { useCart } from '../../contexts/CartContext'
import { useLanguage } from '../../contexts/LanguageContext'
import {
  getTranslatedLabel,
  getTranslatedDescription,
} from '../../utils/getTranslations'
import React from 'react'
import config from '../../config/config'
import { Link } from 'react-router-dom'
import { PRODUCT_PATH } from '../../config/routes'
import { slugify } from '../../utils/utils'
import { formatPrice } from '../../utils/priceUtils'

const CartItem = ({ item, products }) => {
  const { activeLanguage } = useLanguage()
  const { removeItemFromCart, updateItemInCart } = useCart()
  const { t } = useTranslation()

  const productData = products.find((p) => p.name === item.product.name)
  const productVariants = productData?.productVariants || []

  // Filtrage des variantes selon la qualité
  const validProductVariants = productVariants.filter((variant) => {
    // Si item.quality n'est pas défini ou vide, on accepte la variante
    if (!item.quality) {
      return true
    }

    // Récupérer variant.qualities en s'assurant d'avoir un tableau
    let qualities = variant.qualities

    // Si qualities n'est pas défini ou vide (après conversion), on accepte la variante
    if (!qualities) {
      return true
    }

    // Si ce n'est pas déjà un tableau, on tente une conversion (ex : si c'est une chaîne JSON)
    if (!Array.isArray(qualities)) {
      try {
        qualities = JSON.parse(qualities)
      } catch (error) {
        // Si impossible de convertir, on refuse la variante
        return false
      }
    }

    if (!qualities || qualities.length === 0) {
      return true
    }

    return qualities.includes(item.quality)
  })

  const handleProductVariantChange = (event) => {
    const newProductVariantName = event.target.value
    const newProductVariant = validProductVariants.find(
      (v) => v.name === newProductVariantName,
    )

    if (newProductVariant) {
      updateItemInCart({
        ...item,
        productVariant: newProductVariant,
      })
    }
  }

  const handleRemove = () => {
    removeItemFromCart(item.uuid)
  }

  const handleIncrease = () => {
    const newQuantity = item.quantity + 1
    updateItemInCart({ ...item, quantity: newQuantity })
  }

  const handleDecrease = () => {
    const newQuantity = item.quantity - 1
    if (newQuantity > 0) {
      updateItemInCart({ ...item, quantity: newQuantity })
    }
  }

  const handleQuantityChange = (event) => {
    let newQuantity = parseInt(event.target.value, 10)
    if (isNaN(newQuantity) || newQuantity < 1) {
      newQuantity = 1
    }
    updateItemInCart({ ...item, quantity: newQuantity })
  }

  return (
    <li
      className="p-2 green-frame"
      style={{ backgroundColor: 'rgb(220,220,220)' }}
    >
      <div>
        <h2>
          {getTranslatedLabel(item.product, activeLanguage)}
          {item.product.name === 'STANDARD_PHOTO' ? ` n°${item.photoId}` : ''}
        </h2>
        <hr />
      </div>
      <div>
        <p>{getTranslatedDescription(item.product, activeLanguage)}</p>
        <p>
          <Link
            to={`${PRODUCT_PATH.replace(
              ':slug',
              slugify(getTranslatedLabel(item.product, activeLanguage)),
            ).replace(':id', item.product.id)}`}
            className="btn btn-sm btn-primary"
          >
            {t('PRODUCT_SHEET')}
          </Link>
        </p>
      </div>
      <div>
        <h5>{t('AVAILABLE_OPTIONS')}</h5>
        {validProductVariants.length > 0 ? (
          <select
            value={item.productVariant?.name || ''}
            onChange={handleProductVariantChange}
            className="form-select"
          >
            <option value="" disabled>
              {t('CHOOSE')}
            </option>
            {validProductVariants.map((productVariant) => (
              <option key={productVariant.name} value={productVariant.name}>
                {getTranslatedLabel(productVariant, activeLanguage)} -{' '}
                {formatPrice(productVariant.price, activeLanguage)}
              </option>
            ))}
          </select>
        ) : (
          <p>{t('NO_OPTIONS_AVAILABLE')}</p>
        )}
      </div>
      <div style={{ textAlign: 'center' }}>
        {item.photoId && (
          <img
            style={{
              padding: '50px',
              margin: '10px',
              backgroundColor: 'white',
            }}
            src={`${config.photoUrlMedium}${item.photoId}.jpg`}
            alt={t('PRODUCT_IMAGE')}
          />
        )}
      </div>
      <div className="d-flex justify-content-center align-items-center">
        <div className="d-flex align-items-center">
          <button className="btn btn-primary" onClick={handleDecrease}>
            −
          </button>
          <input
            type="number"
            value={item.quantity}
            onChange={handleQuantityChange}
            className="form-control text-center mx-2"
            style={{ width: '60px' }}
          />
          <button className="btn btn-primary" onClick={handleIncrease}>
            +
          </button>
          <button className="btn btn-primary ms-3" onClick={handleRemove}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </li>
  )
}

export default CartItem
